import type { FC } from "react";

interface ActorLevelPipProps {
  level: number;
}

interface TierStyle {
  bg: string;
  text: string;
}

const TIER_STYLES: { max: number; style: TierStyle }[] = [
  { max: 2, style: { bg: "bg-sky-500", text: "text-white" } },
  { max: 5, style: { bg: "bg-emerald-500", text: "text-white" } },
  { max: 9, style: { bg: "bg-amber-500", text: "text-white" } },
  {
    max: Number.POSITIVE_INFINITY,
    style: { bg: "bg-violet-500", text: "text-white" },
  },
];

function resolveStyle(level: number): TierStyle {
  for (const tier of TIER_STYLES) {
    if (level <= tier.max) {
      return tier.style;
    }
  }
  return TIER_STYLES[0].style;
}

/** Tiny corner-pip badge for stacked actor avatars. Mirrors the
 * ActorLevelChip tier palette (sky → emerald → amber → violet) but compresses
 * to a ~14px square anchored to the avatar's top-right. Hidden at level 0 to
 * keep dormant actors visually quiet. Sits at z-10 so it peeks above the next
 * overlapping avatar in `-space-x-1.5` stacks.
 */
export const ActorLevelPip: FC<ActorLevelPipProps> = ({ level }) => {
  if (level <= 0) {
    return null;
  }
  const style = resolveStyle(level);
  const display = level > 99 ? "99+" : String(level);
  return (
    <span
      className={`absolute -right-1 -top-1 z-10 flex h-3.5 min-w-3.5 items-center justify-center px-0.5 ring-1 ring-white dark:ring-slate-900 ${style.bg} ${style.text} font-mono text-[8px] font-bold leading-none tabular-nums`}
      title={`Level ${level}`}
    >
      {display}
    </span>
  );
};
